/**
 * 申请发票（`/invoice` 列表 + `/invoice/:registrationId` 申请面板）
 * -----------------------------------------------------------------------------
 * 2026-10-30 增补（用户拍板）：报名后续随时可能要开发票，入口不能只挂在报名详情页。
 * 宫格「申请发票」进本页 —— 列表即选择器：开票是**报名单维度**的操作，申请前必须先
 * 回答「给哪张单开」，列表只列「报名成功（ENROLLED）」的报名单并按开票状态分组。
 *
 * 「可申请」口径与服务端同源（`isInvoiceableRegistrationStatus`）+ 发票记录互斥：
 *   - 没有发票记录 → 可申请；
 *   - 最新记录为 REJECTED → 驳回不是终态，可重新申请；
 *   - REQUESTED / ISSUED → 不可重复申请（INVOICE-002），归入「已有开票记录」分组。
 *
 * 每行的开票状态用既有的 `GET /api/h5/registrations/{id}/invoice` 逐单查 ——
 * 报名单是学员本人的，量级是个位数，不为它新增契约面（N+1 在此规模无害）。
 *
 * 申请面板直接复用详情页的 `InvoicePanel`（申请表单 / 待开具 / 已开具 / 驳回重申
 * 四态都在组件里），本页只负责选单与装配。
 */
import {
  INVOICE_STATUS_LABEL,
  InvoiceStatus,
  isInvoiceableRegistrationStatus,
} from '@enroll/shared';
import { useNavigate, useParams } from 'react-router-dom';
import { describeApiError } from '../api/errors';
import { useMyInvoice, useMyRegistrations, useRegistration } from '../api/hooks';
import type { InvoiceRecordView, RegistrationBriefView } from '../api/types';
import { InvoicePanel } from '../components/InvoicePanel/InvoicePanel';
import { InvoiceIcon } from '../components/icons';
import { Button, EmptyHint, LoadingBlock, Notice, PageShell } from '../components/layout';

/** 可申请行（无记录 / 已驳回）——点进申请面板 */
function ApplyableRow({
  reg,
  rejected,
  record,
}: {
  reg: RegistrationBriefView;
  rejected: boolean;
  record: InvoiceRecordView | null;
}) {
  const navigate = useNavigate();
  return (
    <button type="button" className="record-card" onClick={() => navigate(`/invoice/${reg.id}`)}>
      <span className="record-card__head">
        <span className="record-card__no">{reg.registrationNo}</span>
        <span className={`pill ${rejected ? 'pill--warning' : 'pill--info'}`}>
          {rejected ? '已驳回 · 可重新申请' : '可申请'}
        </span>
      </span>
      <span className="record-card__meta">
        {reg.enrollTypeName} · {reg.batchName}
      </span>
      <span className="record-card__foot">
        <span className="record-card__amount-label">
          {rejected && record
            ? `原申请：${INVOICE_STATUS_LABEL[record.status]}`
            : '报名成功 · 未开票'}
        </span>
        <span className="record-card__more">去申请</span>
      </span>
    </button>
  );
}

/** 已有开票记录行（待开具 / 已开具）——不可重复申请，引导去报名详情查看 */
function RecordedRow({ reg, record }: { reg: RegistrationBriefView; record: InvoiceRecordView }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="record-card"
      onClick={() => navigate(`/registration/${reg.id}`)}
    >
      <span className="record-card__head">
        <span className="record-card__no">{reg.registrationNo}</span>
        <span className="pill pill--info">{INVOICE_STATUS_LABEL[record.status]}</span>
      </span>
      <span className="record-card__meta">
        {reg.enrollTypeName} · {reg.batchName}
      </span>
      <span className="record-card__foot">
        <span className="record-card__amount-label">已提交过开票申请</span>
        <span className="record-card__more">去详情查看</span>
      </span>
    </button>
  );
}

/** 单行的开票状态装配：查出记录前渲染骨架，查完按归属分组 */
function InvoiceRow({ reg }: { reg: RegistrationBriefView }) {
  const invoice = useMyInvoice(reg.id);

  if (invoice.isPending) {
    return (
      <div className="record-card">
        <LoadingBlock text="正在查询开票状态" />
      </div>
    );
  }
  if (invoice.isError) {
    // 单行自查询失败不整页报错（与 InvoicePanel 同一取向）：降级为该行不可点的提示
    return (
      <div className="record-card">
        <Notice tone="danger">
          {reg.registrationNo}：{describeApiError(invoice.error)}
        </Notice>
      </div>
    );
  }
  const record = invoice.data ?? null;
  const rejected = record?.status === InvoiceStatus.REJECTED;
  if (record && !rejected) {
    return <RecordedRow reg={reg} record={record} />;
  }
  return <ApplyableRow reg={reg} rejected={rejected} record={record} />;
}

/** `/invoice`：报名单选单列表（按开票状态自动分组） */
export function InvoiceApplyList() {
  const registrations = useMyRegistrations();
  const navigate = useNavigate();

  if (registrations.isPending) {
    return (
      <PageShell title="申请发票" back solidNav>
        <LoadingBlock text="正在加载报名单" />
      </PageShell>
    );
  }
  if (registrations.isError) {
    return (
      <PageShell title="申请发票" back solidNav>
        <Notice tone="danger">{describeApiError(registrations.error)}</Notice>
      </PageShell>
    );
  }

  const enrolled = registrations.data.items.filter((reg) =>
    isInvoiceableRegistrationStatus(reg.status),
  );
  if (enrolled.length === 0) {
    return (
      <PageShell title="申请发票" back solidNav>
        <EmptyHint
          icon={<InvoiceIcon size={34} strokeWidth={1.5} />}
          title="暂无可开票的报名单"
          description="报名成功并完成缴费后即可申请发票；如已完成报名仍未看到，请联系客服"
          actionText="查看我的报名"
          onAction={() => navigate('/registration/list')}
          fill
        />
      </PageShell>
    );
  }
  return (
    <PageShell title="申请发票" back solidNav>
      <div className="section-label">选择要开票的报名单（报名成功 · 未开票）</div>
      {enrolled.map((reg) => (
        <InvoiceRow key={reg.id} reg={reg} />
      ))}
      {/* 预期管理：已有记录的单不消失，只是不在「可申请」里 —— 引导去详情看 */}
      <p className="card__desc">
        已有开票记录（待开具 / 已开具）的报名单会显示状态与入口；发票号码由财务开具后回填。
      </p>
    </PageShell>
  );
}

/** `/invoice/:registrationId`：选定报名单后的申请面板（复用详情页 InvoicePanel） */
export function InvoiceApplyDetail() {
  const { registrationId } = useParams<{ registrationId: string }>();
  const navigate = useNavigate();
  const detail = useRegistration(registrationId);

  if (detail.isPending) {
    return (
      <PageShell title="申请发票" back solidNav>
        <LoadingBlock text="正在加载报名单" />
      </PageShell>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <PageShell title="申请发票" back solidNav>
        <Notice tone="danger">{describeApiError(detail.error)}</Notice>
        <Button block variant="ghost" onClick={() => navigate('/invoice')}>
          返回列表
        </Button>
      </PageShell>
    );
  }

  const reg = detail.data;
  if (!isInvoiceableRegistrationStatus(reg.status)) {
    return (
      <PageShell title="申请发票" back solidNav>
        <Notice tone="warning">该报名单当前状态不支持申请发票。</Notice>
        <Button block variant="ghost" onClick={() => navigate('/invoice')}>
          返回列表
        </Button>
      </PageShell>
    );
  }
  return (
    <PageShell title="申请发票" back solidNav>
      <InvoicePanel registrationId={reg.id} canApply applicantName={reg.applicantName} />
    </PageShell>
  );
}
